import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import EmojiPicker from "../components/chat/EmojiPicker";
import MessageBubble from "../components/chat/MessageBubble";
import VoiceRecorder, { canRecordVoice } from "../components/chat/VoiceRecorder";
import * as chatSocket from "../services/chatSocket";
import * as messagesService from "../services/messages";
import "./Messages.css";
import "../components/chat/chat.css";

// New messages, "typing…" and read marks arrive over the chat socket. Polling
// stays as a safety net: fast while the socket is down, slow while it is up,
// and only while the tab is visible.
const THREAD_POLL_MS = { live: 30000, fallback: 4000 };
const LIST_POLL_MS = { live: 60000, fallback: 15000 };
const TYPING_SHOW_MS = 4000;
const TYPING_SEND_EVERY_MS = 2500;
// Messages from the same person this close together share one block.
const GROUP_GAP_MS = 5 * 60 * 1000;
const DEFAULT_REACTIONS = ["\u{1F44D}", "\u2764\uFE0F", "\u{1F602}", "\u{1F62E}", "\u{1F622}", "\u{1F64F}"];
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 10;

const LOCALES = { fr: "fr-FR", en: "en-GB", ar: "ar-u-nu-latn" };

function initials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] || "?").concat(parts[1]?.[0] || "").toUpperCase();
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

function isVisible() {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

// `docked` renders the same chat inside the floating box (ChatDock): the open
// conversation is then kept in local state instead of the URL.
export default function Messages({ docked = false, onClose } = {}) {
  const { accessToken, effectiveRole, user } = useAuth();
  const myId = Number(user?.id);
  const { language, t } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";
  const locale = LOCALES[language] || language || "fr-FR";
  const { conversationId } = useParams();
  const [dockedId, setDockedId] = useState(null);
  const routeId = conversationId ? Number(conversationId) : null;
  const activeId = docked ? dockedId : routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const openConversation = useCallback(
    (id) => {
      if (docked) setDockedId(id);
      else navigate(id ? `/messages/${id}` : "/messages");
    },
    [docked, navigate],
  );

  const tr = useCallback(
    (key, options) => t(`messages.${key}`, options),
    [t],
  );

  const [conversations, setConversations] = useState([]);
  const [canWrite, setCanWrite] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [picking, setPicking] = useState(false);
  const [contacts, setContacts] = useState(null);
  const [starting, setStarting] = useState(false);

  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState("");
  const [threadCanWrite, setThreadCanWrite] = useState(false);

  const [draft, setDraft] = useState("");
  // Files waiting to be sent: [{ key, file, preview }] (preview for images).
  const [files, setFiles] = useState([]);
  const previewUrls = useRef(new Set());
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null); // message being edited
  const [showEmoji, setShowEmoji] = useState(false);
  const [recording, setRecording] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [reactionList, setReactionList] = useState(DEFAULT_REACTIONS);
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  const [atBottom, setAtBottom] = useState(true);
  const [newBelow, setNewBelow] = useState(0);
  const retryData = useRef(new Map());
  const [sendError, setSendError] = useState("");

  const lastIdRef = useRef(0);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const stickToBottom = useRef(true);

  const isStaff = !["teacher", "parent"].includes(effectiveRole);

  const [live, setLive] = useState(chatSocket.isReady());
  useEffect(() => chatSocket.onStatus(setLive), []);
  const [typingUntil, setTypingUntil] = useState(0);
  const lastTypingSent = useRef(0);
  const loadNewerRef = useRef(null);
  const conversationsRef = useRef([]);

  const loadConversations = useCallback(async () => {
    if (!accessToken) return;
    try {
      const payload = await messagesService.fetchConversations({ accessToken });
      setConversations(payload.conversations || []);
      setCanWrite(Boolean(payload.can_write));
      if (Array.isArray(payload.reactions) && payload.reactions.length) {
        setReactionList(payload.reactions);
      }
    } catch {
      // keep the last list; the next poll retries
    } finally {
      setListLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadConversations();
    const timer = setInterval(() => {
      if (isVisible()) loadConversations();
    }, live ? LIST_POLL_MS.live : LIST_POLL_MS.fallback);
    return () => clearInterval(timer);
  }, [live, loadConversations]);

  conversationsRef.current = conversations;

  // Apply an edited/deleted/reacted message, including the quotes of it in replies.
  const applyUpdate = useCallback((updated) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id === updated.id) return updated;
        if (m.reply_to?.id === updated.id) {
          return {
            ...m,
            reply_to: {
              ...m.reply_to,
              deleted: Boolean(updated.deleted),
              body: updated.deleted ? "" : updated.body,
              has_attachment: Boolean(updated.attachments?.length || updated.attachment),
            },
          };
        }
        return m;
      }),
    );
  }, []);

  const markReadLocally = useCallback((id) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === id && c.unread ? { ...c, unread: 0 } : c)),
    );
    window.dispatchEvent(new Event(messagesService.MESSAGES_READ_EVENT));
  }, []);

  // Open thread: full load when it changes, then only newer messages.
  useEffect(() => {
    if (!activeId || !accessToken) {
      setMessages([]);
      return undefined;
    }
    let cancelled = false;
    lastIdRef.current = 0;
    stickToBottom.current = true;
    setTypingUntil(0);
    setMessages([]);
    setFirstUnreadId(null);
    setNewBelow(0);
    setReplyTo(null);
    setEditing(null);
    const unreadAtOpen =
      conversationsRef.current.find((c) => c.id === activeId)?.unread || 0;
    setThreadError("");
    setThreadLoading(true);
    setSendError("");

    const load = async (initial) => {
      try {
        const payload = await messagesService.fetchMessages({
          accessToken,
          conversationId: activeId,
          after: initial ? undefined : lastIdRef.current || undefined,
        });
        if (cancelled) return;
        const incoming = payload.messages || [];
        setThreadCanWrite(Boolean(payload.can_write));
        if (incoming.length) {
          lastIdRef.current = incoming[incoming.length - 1].id;
          if (initial && unreadAtOpen > 0) {
            const firstUnread = incoming[Math.max(0, incoming.length - unreadAtOpen)];
            if (firstUnread && !firstUnread.is_mine) setFirstUnreadId(firstUnread.id);
          }
          setMessages((prev) => {
            if (initial) return incoming;
            const known = new Set(prev.map((m) => m.id));
            return [...prev, ...incoming.filter((m) => !known.has(m.id))];
          });
        }
        if (initial || incoming.length) markReadLocally(activeId);
      } catch (e) {
        if (!cancelled && initial) {
          setThreadError(e.status === 404 ? "notFound" : "loadError");
        }
      } finally {
        if (!cancelled && initial) setThreadLoading(false);
      }
    };

    load(true);
    loadNewerRef.current = () => load(false);
    return () => {
      cancelled = true;
      loadNewerRef.current = null;
    };
  }, [activeId, accessToken, markReadLocally]);

  // Safety-net polling of the open thread.
  useEffect(() => {
    if (!activeId) return undefined;
    const timer = setInterval(() => {
      if (isVisible()) loadNewerRef.current?.();
    }, live ? THREAD_POLL_MS.live : THREAD_POLL_MS.fallback);
    return () => clearInterval(timer);
  }, [activeId, live]);

  // A reconnect may have missed events: catch up once.
  useEffect(() => {
    if (!live) return;
    loadConversations();
    loadNewerRef.current?.();
  }, [live, loadConversations]);

  // Live events from the chat socket.
  useEffect(() => {
    return chatSocket.subscribe((event) => {
      const conversationId = Number(event?.conversation_id);
      if (event?.type === "message.new") {
        const message = {
          ...event.message,
          is_mine: Number(event.message?.sender?.id) === myId,
        };
        if (conversationId === activeId) {
          setTypingUntil(0);
          setMessages((prev) =>
            prev.some((m) => m.id === message.id) ? prev : [...prev, message],
          );
          if (!stickToBottom.current && !message.is_mine) setNewBelow((n) => n + 1);
          lastIdRef.current = Math.max(lastIdRef.current, Number(message.id) || 0);
          // Mark it read on the server (and in the other person's "Seen").
          if (!message.is_mine && isVisible()) {
            messagesService
              .fetchMessages({ accessToken, conversationId, after: message.id })
              .catch(() => {});
          }
        }
        if (!conversationsRef.current.some((c) => c.id === conversationId)) {
          // A brand-new conversation someone else started.
          loadConversations();
          return;
        }
        setConversations((prev) => {
          const current = prev.find((c) => c.id === conversationId);
          if (!current) return prev;
          const counts = !message.is_mine && conversationId !== activeId;
          const updated = {
            ...current,
            last_message: {
              body: message.body,
              attachment_name: message.attachment?.name || "",
              is_mine: message.is_mine,
            },
            last_message_at: message.created_at,
            unread: counts ? (current.unread || 0) + 1 : current.unread,
          };
          return [updated, ...prev.filter((c) => c.id !== conversationId)];
        });
      } else if (event?.type === "message.updated") {
        const updated = {
          ...event.message,
          is_mine: Number(event.message?.sender?.id) === myId,
        };
        if (conversationId === activeId) applyUpdate(updated);
        if (updated.deleted) loadConversations();
      } else if (event?.type === "presence") {
        setConversations((prev) =>
          prev.map((c) =>
            Number(c.other?.id) === Number(event.user_id)
              ? { ...c, other_presence: { online: event.online, last_seen: event.last_seen } }
              : c,
          ),
        );
      } else if (event?.type === "typing") {
        if (conversationId === activeId) setTypingUntil(Date.now() + TYPING_SHOW_MS);
      } else if (event?.type === "conversation.read") {
        if (Number(event.user_id) !== myId) {
          const now = new Date().toISOString();
          setConversations((prev) =>
            prev.map((c) => (c.id === conversationId ? { ...c, other_read_at: now } : c)),
          );
        }
      }
    });
  }, [accessToken, activeId, applyUpdate, loadConversations, myId]);

  // Hide "typing…" when it times out.
  useEffect(() => {
    if (!typingUntil) return undefined;
    const timer = setTimeout(() => setTypingUntil(0), Math.max(0, typingUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [typingUntil]);

  const onDraftChange = (value) => {
    setDraft(value);
    const now = Date.now();
    if (value.trim() && activeId && now - lastTypingSent.current > TYPING_SEND_EVERY_MS) {
      lastTypingSent.current = now;
      chatSocket.send({ type: "typing", conversation_id: activeId });
    }
  };

  // Keep the newest message in view unless the reader scrolled up.
  useEffect(() => {
    const el = bodyRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onBodyScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickToBottom.current = bottom;
    setAtBottom(bottom);
    if (bottom) setNewBelow(0);
  };

  // When the composer grows (files, several lines) keep the newest message in view.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      if (stickToBottom.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeId, threadLoading]);

  const scrollToLatest = () => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    stickToBottom.current = true;
    setAtBottom(true);
    setNewBelow(0);
  };

  const jumpTo = (messageId) => {
    const el = document.getElementById(`msg-${messageId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("is-flash");
    setTimeout(() => el.classList.remove("is-flash"), 1400);
  };

  const openPicker = async () => {
    setPicking(true);
    if (contacts) return;
    try {
      const payload = await messagesService.fetchContacts({ accessToken });
      setContacts(payload.contacts || []);
    } catch {
      setContacts([]);
    }
  };

  const startWith = useCallback(
    async (userId, studentId) => {
      setStarting(true);
      try {
        const payload = await messagesService.startConversation({
          accessToken,
          userId,
          studentId,
        });
        const created = payload.conversation;
        setConversations((prev) =>
          prev.some((c) => c.id === created.id) ? prev : [created, ...prev],
        );
        setPicking(false);
        openConversation(created.id);
        setTimeout(() => inputRef.current?.focus(), 50);
      } catch {
        // stay on the picker
      } finally {
        setStarting(false);
      }
    },
    [accessToken, openConversation],
  );

  // Links from other pages: /messages?to=<userId>&student=<studentId>
  useEffect(() => {
    if (docked) return;
    const query = new URLSearchParams(location.search);
    const to = Number(query.get("to"));
    const student = Number(query.get("student"));
    if (to && student && accessToken) startWith(to, student);
  }, [docked, location.search, accessToken, startWith]);

  const updateListPreview = useCallback((conversationId, message) => {
    setConversations((prev) => {
      const current = prev.find((c) => c.id === conversationId);
      if (!current) return prev;
      const updated = {
        ...current,
        last_message: {
          body: message.body,
          attachment_name: message.attachments?.[0]?.name || message.attachment?.name || "",
          is_mine: true,
        },
        last_message_at: message.created_at,
      };
      return [updated, ...prev.filter((c) => c.id !== conversationId)];
    });
  }, []);

  const deliver = async (tempId, { body, files: uploads = [], replyId }) => {
    const conversationId = activeId;
    try {
      const payload = await messagesService.sendMessage({
        accessToken,
        conversationId,
        body,
        files: uploads,
        replyTo: replyId,
      });
      const message = payload.message;
      retryData.current.delete(tempId);
      setMessages((prev) => {
        const withoutTemp = prev.filter((m) => m.id !== tempId);
        return withoutTemp.some((m) => m.id === message.id) ? withoutTemp : [...withoutTemp, message];
      });
      lastIdRef.current = Math.max(lastIdRef.current, message.id);
      updateListPreview(conversationId, message);
    } catch (e) {
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, pending: false, failed: true } : m)),
      );
      setSendError(e.message || tr("sendError"));
    }
  };

  const send = async () => {
    const body = draft.trim();
    if (!activeId) return;

    if (editing) {
      if (!body && !(editing.attachments?.length || editing.attachment)) return;
      const target = editing;
      setEditing(null);
      setDraft("");
      if (body === target.body) return;
      applyUpdate({ ...target, body, edited: true });
      try {
        await messagesService.editMessage({ accessToken, messageId: target.id, body });
      } catch (e) {
        setMessages((prev) => prev.map((m) => (m.id === target.id ? target : m)));
        setSendError(e.message || tr("sendError"));
      }
      return;
    }

    if (!body && !files.length) return;
    setSendError("");
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const data = { body, files: files.map((f) => f.file), replyId: replyTo?.id };
    retryData.current.set(tempId, data);
    stickToBottom.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        pending: true,
        is_mine: true,
        body,
        created_at: new Date().toISOString(),
        sender: { id: myId, name: tr("you") },
        attachment: null,
        localFiles: files.map((f) => ({
          id: f.key,
          name: f.file.name,
          content_type: f.file.type,
          size_bytes: f.file.size,
          preview: f.preview,
        })),
        reply_to: replyTo
          ? {
              id: replyTo.id,
              sender: replyTo.sender,
              body: replyTo.body,
              deleted: replyTo.deleted,
              has_attachment: Boolean(replyTo.attachments?.length || replyTo.attachment),
            }
          : null,
        reactions: [],
      },
    ]);
    setDraft("");
    setFiles([]);
    setReplyTo(null);
    setShowEmoji(false);
    inputRef.current?.focus();
    await deliver(tempId, data);
  };

  const retry = (tempId) => {
    const data = retryData.current.get(tempId);
    if (!data) return;
    setSendError("");
    setMessages((prev) =>
      prev.map((m) => (m.id === tempId ? { ...m, pending: true, failed: false } : m)),
    );
    deliver(tempId, data);
  };

  const sendVoice = async (voiceFile) => {
    setRecording(false);
    if (!activeId || !voiceFile?.size) return;
    const tempId = `tmp-${Date.now()}`;
    const data = { body: "", files: [voiceFile], replyId: replyTo?.id };
    retryData.current.set(tempId, data);
    stickToBottom.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        pending: true,
        is_mine: true,
        body: "",
        created_at: new Date().toISOString(),
        sender: { id: myId, name: tr("you") },
        attachment: null,
        localFiles: [
          { id: "voice", name: tr("voiceMessage"), content_type: voiceFile.type, size_bytes: voiceFile.size },
        ],
        reply_to: null,
        reactions: [],
      },
    ]);
    setReplyTo(null);
    await deliver(tempId, data);
  };

  const react = async (message, emoji) => {
    // Show it at once; the server's answer (and the socket) confirm it.
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== message.id) return m;
        const others = (m.reactions || [])
          .map((r) => ({ ...r, user_ids: (r.user_ids || []).filter((u) => u !== myId) }))
          .map((r) => ({ ...r, count: r.user_ids.length }))
          .filter((r) => r.count > 0);
        const hadSame = (m.reactions || []).some((r) => r.emoji === emoji && r.user_ids?.includes(myId));
        if (hadSame) return { ...m, reactions: others };
        const target = others.find((r) => r.emoji === emoji);
        const next = target
          ? others.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1, user_ids: [...r.user_ids, myId] } : r))
          : [...others, { emoji, count: 1, user_ids: [myId] }];
        return { ...m, reactions: next };
      }),
    );
    try {
      const payload = await messagesService.reactToMessage({ accessToken, messageId: message.id, emoji });
      const updated = { ...payload.message, is_mine: payload.message.sender?.id === myId };
      setMessages((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    } catch {
      setMessages((prev) => prev.map((m) => (m.id === message.id ? message : m)));
    }
  };

  const removeMessage = async (message) => {
    if (!window.confirm(tr("deleteConfirm"))) return;
    applyUpdate({ ...message, deleted: true, body: "", attachment: null, reactions: [] });
    try {
      await messagesService.deleteMessage({ accessToken, messageId: message.id });
      loadConversations();
    } catch (e) {
      setMessages((prev) => prev.map((m) => (m.id === message.id ? message : m)));
      setSendError(e.message || tr("sendError"));
    }
  };

  const startEdit = (message) => {
    setReplyTo(null);
    setEditing(message);
    setDraft(message.body || "");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const startReply = (message) => {
    setEditing(null);
    setReplyTo(message);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + emoji + draft.slice(end);
    onDraftChange(next);
    setTimeout(() => {
      el?.focus();
      const pos = start + emoji.length;
      el?.setSelectionRange(pos, pos);
    }, 0);
  };

  // Add picked / pasted / dropped files (several at once).
  const addFiles = (list) => {
    const picked = [...(list || [])].filter(Boolean);
    if (!picked.length) return;
    const tooBig = picked.filter((f) => f.size > MAX_FILE_BYTES);
    const ok = picked.filter((f) => f.size <= MAX_FILE_BYTES);
    setEditing(null);
    setFiles((prev) => {
      const room = Math.max(0, MAX_FILES - prev.length);
      const added = ok.slice(0, room).map((f) => {
        let preview = "";
        if (f.type.startsWith("image/")) {
          preview = URL.createObjectURL(f);
          previewUrls.current.add(preview);
        }
        return { key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file: f, preview };
      });
      if (ok.length > room) setSendError(tr("tooManyFiles", { count: MAX_FILES }));
      else if (tooBig.length) setSendError(tr("fileTooBig"));
      else setSendError("");
      return [...prev, ...added];
    });
  };

  const removeFile = (key) => {
    setFiles((prev) => prev.filter((f) => f.key !== key));
  };

  // Free image previews when leaving the page.
  useEffect(() => {
    const urls = previewUrls.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const onPaste = (event) => {
    const pasted = [...(event.clipboardData?.files || [])];
    if (pasted.length) {
      event.preventDefault();
      addFiles(pasted);
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    if (!threadCanWrite) return;
    addFiles(event.dataTransfer?.files);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    } else if (e.key === "Escape" && (replyTo || editing)) {
      e.preventDefault();
      if (editing) setDraft("");
      setReplyTo(null);
      setEditing(null);
    } else if (e.key === "ArrowUp" && !draft) {
      // Like other chat apps: up arrow edits my last message.
      const last = [...messages].reverse().find((m) => m.is_mine && !m.deleted && !m.pending && m.body);
      if (last) {
        e.preventDefault();
        startEdit(last);
      }
    }
  };

  const onPickFile = (e) => {
    const picked = [...(e.target.files || [])];
    e.target.value = "";
    addFiles(picked);
  };

  const presenceLabel = (presence) => {
    if (!presence) return "";
    if (presence.online) return tr("online");
    if (!presence.last_seen) return "";
    const seenAt = new Date(presence.last_seen);
    const minutes = Math.round((Date.now() - seenAt.getTime()) / 60000);
    if (minutes < 1) return tr("activeNow");
    if (minutes < 60) return tr("activeMinutes", { count: minutes });
    return tr("lastSeen", { when: timeLabel(presence.last_seen, { withDay: true }) });
  };

  // Grow the text box with its content (up to the CSS max height).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflowY = el.scrollHeight > 140 ? "auto" : "hidden";
  }, [draft]);

  const timeLabel = useCallback(
    (iso, { withDay = false } = {}) => {
      if (!iso) return "";
      const date = new Date(iso);
      const now = new Date();
      const time = date.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      });
      if (!withDay) return time;
      if (sameDay(date, now)) return time;
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      if (sameDay(date, yesterday)) return tr("yesterday");
      return date.toLocaleDateString(locale, { day: "numeric", month: "short" });
    },
    [locale, tr],
  );

  const dayLabel = useCallback(
    (iso) => {
      const date = new Date(iso);
      const now = new Date();
      if (sameDay(date, now)) return tr("today");
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      if (sameDay(date, yesterday)) return tr("yesterday");
      return date.toLocaleDateString(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
    },
    [locale, tr],
  );

  const titleFor = useCallback(
    (c) => c?.other?.name || `${c?.teacher?.name} · ${c?.parent?.name}`,
    [],
  );

  const visibleConversations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) =>
      [titleFor(c), c.student?.name].join(" ").toLowerCase().includes(q),
    );
  }, [conversations, search, titleFor]);

  const contactGroups = useMemo(() => {
    const groups = new Map();
    (contacts || []).forEach((c) => {
      const key = c.student_id;
      if (!groups.has(key)) groups.set(key, { name: c.student_name, items: [] });
      groups.get(key).items.push(c);
    });
    return [...groups.values()];
  }, [contacts]);

  const active = conversations.find((c) => c.id === activeId) || null;
  const otherRole =
    active?.my_side === "teacher" ? tr("roleParent") : tr("roleTeacher");
  const subtitle = isStaff
    ? tr("subtitleStaff")
    : effectiveRole === "teacher"
      ? tr("subtitleTeacher")
      : tr("subtitleParent");

  const bubbleLabels = {
    deleted: tr("deletedMessage"),
    attachment: tr("attachment"),
    edited: tr("edited"),
    sending: tr("sending"),
    failed: tr("failedRetry"),
    react: tr("react"),
    reply: tr("reply"),
    more: tr("more"),
    copy: tr("copy"),
    copied: tr("copied"),
    edit: tr("edit"),
    delete: tr("delete"),
    reactionList,
  };

  const renderThread = () => {
    if (picking) {
      return (
        <div className="messages__picker">
          <div className="messages__pickerHead">
            <h2>{tr("pickTitle")}</h2>
            <button
              type="button"
              className="btn ghost"
              onClick={() => setPicking(false)}
            >
              {tr("cancel")}
            </button>
          </div>
          {contacts === null ? (
            <div className="messages__spinner" aria-hidden="true" />
          ) : contactGroups.length === 0 ? (
            <p className="messages__muted">
              {effectiveRole === "teacher"
                ? tr("noContactsTeacher")
                : tr("noContactsParent")}
            </p>
          ) : (
            contactGroups.map((group) => (
              <div className="messages__pickerGroup" key={group.name}>
                <div className="messages__pickerChild">
                  <i className="fa-solid fa-child" aria-hidden="true" />
                  {group.name}
                </div>
                {group.items.map((c) => (
                  <button
                    type="button"
                    key={`${c.user_id}-${c.student_id}`}
                    className="messages__contact"
                    disabled={starting}
                    onClick={() => startWith(c.user_id, c.student_id)}
                  >
                    <span className="messages__avatar">{initials(c.name)}</span>
                    <span className="messages__contactText">
                      <strong>{c.name}</strong>
                      <small>
                        {c.role === "teacher"
                          ? tr("roleTeacher")
                          : tr("roleParent")}
                      </small>
                    </span>
                    <i className="fa-solid fa-pen" aria-hidden="true" />
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      );
    }

    if (!activeId) {
      return (
        <div className="messages__placeholder">
          <i className="fa-regular fa-comments" aria-hidden="true" />
          <p>{tr("selectConversation")}</p>
        </div>
      );
    }

    if (threadError) {
      return (
        <div className="messages__placeholder">
          <i className="fa-regular fa-circle-xmark" aria-hidden="true" />
          <p>{tr("loadError")}</p>
        </div>
      );
    }

    let lastDay = "";
    // "Seen" goes under my last message once the other person read it.
    const lastMine = [...messages].reverse().find((m) => m.is_mine);
    const seen =
      !isStaff &&
      lastMine &&
      active?.other_read_at &&
      new Date(active.other_read_at) >= new Date(lastMine.created_at);
    const typing = typingUntil > Date.now();
    return (
      <>
        <header className="messages__threadHead">
          <button
            type="button"
            className="messages__back"
            onClick={() => openConversation(null)}
            aria-label={tr("back")}
          >
            <i className="fa-solid fa-arrow-left" aria-hidden="true" />
          </button>
          <span className="messages__avatar">
            {initials(titleFor(active))}
            {active?.other_presence?.online ? <span className="messages__online" /> : null}
          </span>
          <div className="messages__threadTitle">
            <strong>{active ? titleFor(active) : ""}</strong>
            {typing ? (
              <small className="messages__typingText">{tr("typing")}</small>
            ) : active ? (
              <small>
                {!isStaff && presenceLabel(active.other_presence) ? (
                  <span className={active.other_presence?.online ? "messages__onlineText" : ""}>
                    {presenceLabel(active.other_presence)} ·{" "}
                  </span>
                ) : null}
                {isStaff ? null : <>{otherRole} · </>}
                {tr("aboutChild", { name: active.student?.name })}
              </small>
            ) : null}
          </div>
        </header>

        <div
          className={`messages__body${dragOver ? " is-dragOver" : ""}`}
          ref={bodyRef}
          onScroll={onBodyScroll}
          // Images arrive after the scroll: stay at the bottom if we were there.
          onLoadCapture={() => {
            const el = bodyRef.current;
            if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
          }}
          onDragOver={(e) => {
            if (!threadCanWrite) return;
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          {dragOver ? <div className="messages__dropHint">{tr("dropHere")}</div> : null}
          {threadLoading ? (
            <div className="messages__spinner" aria-hidden="true" />
          ) : messages.length === 0 ? (
            <p className="messages__muted messages__center">
              {tr("noMessages")}
            </p>
          ) : (
            messages.map((m, index) => {
              const day = new Date(m.created_at).toDateString();
              const showDay = day !== lastDay;
              lastDay = day;
              const mine = isStaff
                ? m.sender?.id === active?.teacher?.id
                : m.is_mine;
              const prev = messages[index - 1];
              const grouped =
                !showDay &&
                prev &&
                prev.sender?.id === m.sender?.id &&
                !prev.deleted &&
                new Date(m.created_at) - new Date(prev.created_at) < GROUP_GAP_MS &&
                m.id !== firstUnreadId;
              const shown = m;
              return (
                <React.Fragment key={m.id}>
                  {showDay ? (
                    <div className="messages__day">
                      <span>{dayLabel(m.created_at)}</span>
                    </div>
                  ) : null}
                  {m.id === firstUnreadId ? (
                    <div className="messages__unreadLine">
                      <span>{tr("newMessages")}</span>
                    </div>
                  ) : null}
                  <MessageBubble
                    message={shown}
                    mine={mine}
                    myId={myId}
                    grouped={grouped}
                    showSender={isStaff && !grouped}
                    canAct={threadCanWrite}
                    reactions={m.reactions || []}
                    time={timeLabel(m.created_at)}
                    labels={bubbleLabels}
                    onReact={(emoji) => react(m, emoji)}
                    onReply={() => startReply(m)}
                    onEdit={() => startEdit(m)}
                    onDelete={() => removeMessage(m)}
                    onRetry={() => retry(m.id)}
                    onJumpTo={jumpTo}
                  />
                  {seen && m.id === lastMine.id ? (
                    <div className="messages__seen">
                      <i className="fa-solid fa-check-double" aria-hidden="true" />
                      {tr("seen")}
                    </div>
                  ) : null}
                </React.Fragment>
              );
            })
          )}
          {!atBottom && messages.length ? (
            // Sticky inside the scroll area, so it always sits just above the composer.
            <div className="messages__jumpWrap">
              <button type="button" className="messages__jump" onClick={scrollToLatest}>
                <i className="fa-solid fa-arrow-down" aria-hidden="true" />
                {newBelow ? tr("newBelow", { count: newBelow }) : tr("jumpToLatest")}
              </button>
            </div>
          ) : null}
          {typing ? (
            <div className="messages__row">
              <div className="messages__typing" aria-label={tr("typing")}>
                <span />
                <span />
                <span />
              </div>
            </div>
          ) : null}
        </div>


        {threadCanWrite ? (
          <form
            className="messages__composer"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            {replyTo || editing ? (
              <div className="messages__context">
                <i
                  className={`fa-solid ${editing ? "fa-pen" : "fa-reply"}`}
                  aria-hidden="true"
                />
                <div>
                  <strong>
                    {editing
                      ? tr("editing")
                      : tr("replyingTo", {
                          name: replyTo.is_mine ? tr("yourself") : replyTo.sender?.name,
                        })}
                  </strong>
                  <span>{(editing || replyTo).body || tr("attachment")}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (editing) setDraft("");
                    setReplyTo(null);
                    setEditing(null);
                  }}
                  aria-label={tr("cancel")}
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </div>
            ) : null}
            {files.length ? (
              <div className="messages__pending" aria-label={tr("filesToSend")}>
                {files.map((f) => (
                  <div
                    className="messages__pendingTile"
                    key={f.key}
                    title={`${f.file.name} (${formatSize(f.file.size)})`}
                  >
                    {f.preview ? (
                      <img src={f.preview} alt={f.file.name} />
                    ) : (
                      <span className="messages__pendingFile">
                        <i className="fa-solid fa-file" aria-hidden="true" />
                        <strong>{(/\.([a-z0-9]{1,5})$/i.exec(f.file.name)?.[1] || "").toUpperCase()}</strong>
                        <small>{f.file.name}</small>
                      </span>
                    )}
                    <button
                      type="button"
                      className="messages__pendingRemove"
                      onClick={() => removeFile(f.key)}
                      aria-label={`${tr("removeFile")} ${f.file.name}`}
                      title={tr("removeFile")}
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                  </div>
                ))}
                {files.length < MAX_FILES ? (
                  <button
                    type="button"
                    className="messages__pendingAdd"
                    onClick={() => fileRef.current?.click()}
                    aria-label={tr("addFiles")}
                    title={tr("addFiles")}
                  >
                    <i className="fa-solid fa-plus" aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            ) : null}
            {sendError ? (
              <p className="messages__error" role="alert">
                {sendError}
              </p>
            ) : null}
            {recording ? (
              <VoiceRecorder
                onDone={sendVoice}
                onCancel={() => setRecording(false)}
                onError={() => {
                  setRecording(false);
                  setSendError(tr("micDenied"));
                }}
                labels={{ cancel: tr("cancel"), recording: tr("recording"), send: tr("send") }}
              />
            ) : (
              <div className="messages__composerRow">
                <button
                  type="button"
                  className="messages__iconBtn"
                  onClick={() => fileRef.current?.click()}
                  aria-label={tr("attach")}
                  title={tr("attach")}
                  disabled={Boolean(editing)}
                >
                  <i className="fa-solid fa-paperclip" aria-hidden="true" />
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  hidden
                  onChange={onPickFile}
                  accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                />
                <div className="messages__inputWrap">
                  <textarea
                    ref={inputRef}
                    rows={1}
                    value={draft}
                    maxLength={5000}
                    placeholder={tr("placeholder")}
                    onChange={(e) => onDraftChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    onPaste={onPaste}
                    aria-label={tr("placeholder")}
                  />
                  <button
                    type="button"
                    className="messages__emojiBtn"
                    onClick={() => setShowEmoji((v) => !v)}
                    aria-label={tr("emoji")}
                    title={tr("emoji")}
                    aria-expanded={showEmoji}
                  >
                    <i className="fa-regular fa-face-smile" aria-hidden="true" />
                  </button>
                  {showEmoji ? (
                    <EmojiPicker
                      onPick={insertEmoji}
                      onClose={() => setShowEmoji(false)}
                      labels={{
                        title: tr("emoji"),
                        recent: tr("emojiRecent"),
                        smileys: tr("emojiSmileys"),
                        gestures: tr("emojiGestures"),
                        hearts: tr("emojiHearts"),
                        school: tr("emojiSchool"),
                        nature: tr("emojiNature"),
                        food: tr("emojiFood"),
                      }}
                    />
                  ) : null}
                </div>
                {!draft.trim() && !files.length && !editing && canRecordVoice() ? (
                  <button
                    type="button"
                    className="messages__send"
                    onClick={() => {
                      setSendError("");
                      setRecording(true);
                    }}
                    aria-label={tr("recordVoice")}
                    title={tr("recordVoice")}
                  >
                    <i className="fa-solid fa-microphone" aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="messages__send"
                    disabled={!draft.trim() && !files.length}
                    aria-label={editing ? tr("saveEdit") : tr("send")}
                    title={editing ? tr("saveEdit") : tr("send")}
                  >
                    <i
                      className={`fa-solid ${editing ? "fa-check" : "fa-paper-plane"}`}
                      aria-hidden="true"
                    />
                  </button>
                )}
              </div>
            )}
            <small className="messages__hint">{tr("enterHint")}</small>
          </form>
        ) : !threadLoading ? (
          <p className="messages__readOnly">
            <i className="fa-solid fa-eye" aria-hidden="true" /> {tr("readOnly")}
          </p>
        ) : null}
      </>
    );
  };

  const Shell = docked ? "div" : "main";
  return (
    <Shell
      className={`${docked ? "messages-dock" : "messages-page"}${
        activeId || picking ? " has-thread" : ""
      }`}
      dir={dir}
    >
      <div
        className={
          docked
            ? "messages-dock__container"
            : "container messages-page__container"
        }
      >
        {docked ? (
          <header className="messages-dock__header">
            <strong>{tr("title")}</strong>
            <button
              type="button"
              className="messages-dock__headBtn"
              onClick={() => {
                navigate(activeId ? `/messages/${activeId}` : "/messages");
                if (onClose) onClose();
              }}
              aria-label={tr("openFull")}
              title={tr("openFull")}
            >
              <i className="fa-solid fa-up-right-and-down-left-from-center" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="messages-dock__headBtn"
              onClick={onClose}
              aria-label={tr("close")}
              title={tr("close")}
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </header>
        ) : (
          <header className="messages-page__header">
            <h1>{tr("title")}</h1>
            <p>{subtitle}</p>
          </header>
        )}

        <div
          className={`messages${activeId || picking ? " has-thread" : ""}`}
        >
          <aside className="messages__list">
            <div className="messages__listHead">
              <div className="messages__search">
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={tr("searchPlaceholder")}
                  aria-label={tr("searchPlaceholder")}
                />
              </div>
              {canWrite ? (
                <button
                  type="button"
                  className="btn primary messages__new"
                  onClick={openPicker}
                >
                  <i className="fa-solid fa-plus" aria-hidden="true" />
                  {tr("newConversation")}
                </button>
              ) : null}
            </div>

            <div className="messages__items">
              {listLoading ? (
                <div className="messages__spinner" aria-hidden="true" />
              ) : visibleConversations.length === 0 ? (
                <div className="messages__empty">
                  <p>{tr("empty")}</p>
                  {canWrite && !search ? (
                    <small>
                      {effectiveRole === "teacher"
                        ? tr("emptyHintTeacher")
                        : tr("emptyHintParent")}
                    </small>
                  ) : null}
                </div>
              ) : (
                visibleConversations.map((c) => {
                  const last = c.last_message;
                  const preview = last
                    ? `${last.is_mine ? `${tr("you")} : ` : ""}${
                        last.deleted ? tr("deletedMessage") : last.body || last.attachment_name
                      }`
                    : "";
                  return (
                    <button
                      type="button"
                      key={c.id}
                      className={`messages__item${
                        c.id === activeId && !picking ? " is-active" : ""
                      }${c.unread ? " is-unread" : ""}`}
                      onClick={() => {
                        setPicking(false);
                        openConversation(c.id);
                      }}
                    >
                      <span className="messages__avatar">
                        {initials(titleFor(c))}
                        {c.other_presence?.online ? <span className="messages__online" /> : null}
                      </span>
                      <span className="messages__itemText">
                        <span className="messages__itemTop">
                          <strong>{titleFor(c)}</strong>
                          <time>
                            {timeLabel(c.last_message_at, { withDay: true })}
                          </time>
                        </span>
                        <span className="messages__child">
                          <i className="fa-solid fa-child" aria-hidden="true" />
                          {c.student?.name}
                        </span>
                        <span className="messages__itemBottom">
                          <span className="messages__preview">{preview}</span>
                          {c.unread ? (
                            <span className="messages__badge">{c.unread}</span>
                          ) : null}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <section className="messages__thread">{renderThread()}</section>
        </div>
      </div>
    </Shell>
  );
}
